'use strict';

// external imports
import React, {
    useCallback,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {createPortal} from 'react-dom'
import {
    useDispatch,
    useSelector
} from 'react-redux';
import {keys} from 'ts-transformer-keys';
import _ from 'lodash';
import parse from 'html-react-parser';
import {v4 as uuidV4} from 'uuid';

// prime react imports
import {localeOption} from 'primereact/api';
import {Button} from 'primereact/button';
import {Card} from 'primereact/card';
import {ContextMenu} from 'primereact/contextmenu';
import {
    MenuItem,
    MenuItemCommandEvent
} from 'primereact/menuitem';
import {Panel} from 'primereact/panel';
import {Tooltip} from 'primereact/tooltip';
import {
    Tree,
    TreeEventNodeEvent,
    TreeExpandedEvent,
    TreeExpandedKeysType,
    TreeSelectionEvent,
    TreeTogglerTemplateOptions
} from 'primereact/tree';
import {TreeNode as PrimeTreeNode} from 'primereact/treenode';

// shared imports
import {__} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';
import Item from '~/shared/src/ci/Item';
import {
    DataObjectProperties,
    DataStructure,
    extractDataObjectsFromDataStructure
} from '~/shared/src/utilities/dataStructure';
import dataExtraction from '~/shared/src/utilities/dataExtraction';
import {TreeView} from '~/shared/src/utilities/modules/treeDesigner';
import {
    DynamicNode,
    TreeNode
} from '~/shared/src/utilities/treeViewNodes';

// frontend imports
import ContentButton, {ReferenceType as ContentButtonType} from '~/frontend/src/components/base/shared/ContentButton';
import LoadingMaskPanel from '~/frontend/src/components/base/LoadingMaskPanel';
import TreeViewPanel, {
    AllProps as TreeViewPanelProps
} from '~/frontend/src/modules/panels/TreeView';

import {
    getIconSpan,
    resolveLigatureIcon
} from '~/frontend/src/utilities/renderHelpers';
import {isInvalidHTML} from '~/frontend/src/utilities/valueRenderers';
import {getDataRecursive} from '~/frontend/src/utilities/dataUtils';
import {getSubPanelProps} from '~/frontend/src/components/base/page/PageTabPanel/utilities';

import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import {PanelElusiveDataState} from '~/frontend/src/reducers/elusiveData';
import origSetParam, {
    SetParamAction
} from '~/frontend/src/actions/panel/common/setParam';
import origSetParams, {
    SetParamsAction
} from '~/frontend/src/actions/panel/common/setParams';
import clearPanelProps from '~/frontend/src/actions/panelManager/clearPanelProps';
import {
    clearElusiveData,
    clearElusiveDataForSubpanels
} from '~/frontend/src/actions/elusiveData';
import addItem from '~/frontend/src/actions/data/addItem';
import deleteItem from '~/frontend/src/actions/data/deleteItem';
import updateItem from '~/frontend/src/actions/data/updateItem';
import addReference from '~/frontend/src/actions/data/addReference';
import deleteReference from '~/frontend/src/actions/data/deleteReference';
import updateReference from '~/frontend/src/actions/data/updateReference';


type ExtendTreeNode = Omit<PrimeTreeNode, 'key' | 'label'> & {
    key : string;
    label : string;
    deleted : boolean;
    dirty : boolean;
    error? : {
        count : number;
        tooltip? : React.ReactNode;
    };
    tooltip? : React.ReactNode;
    data? : TreeNode | DynamicNode;
    buttons? : React.ReactNode[];
    children? : ExtendTreeNode[];
};

type TreeNodeMap = Map<string, ( DynamicNode | TreeNode )>;
type NodeButtons = {
    [ buttonId : string ] : {
        justInteracted : boolean;
        content? : ContentButtonType;
    }
};
type NodeButtonPanels = {
    [ panelId : string ] : Panel;
};
type TreeDragInfo = {
    nodeKey : string;

    dragOverZone : 'delete' | 'node' | 'nothing';
    dragOverNodeKey? : string;

    treeDataNode : ExtendTreeNode;
};

type DragGhostData = {
    icon : React.ReactNode;
    label : string;
    initialLeft : number;
    initialTop : number;
};

type DropZonesRect = {
    top : number;
    height : number;
    deleteWidth : number;
    cancelWidth : number;
};

type MouseEventHandler = ( event : MouseEvent ) => void;

export type Mode = 'design' | 'input';

export type DefaultProps = {
    additionalElusiveDataContexts? : string[];
    mode? : Mode;
    readOnly? : boolean;
    scrollTop? : number;
    scrollLeft? : number;

};
export const defaultProps : Required<DefaultProps> = {
    additionalElusiveDataContexts: [],
    mode                         : 'design',
    readOnly                     : false,
    scrollTop                    : 0,
    scrollLeft                   : 0
};

export type Props = {
    uid : string;
    parentUid : string;

    dataContext : string;

    rootItemId : number;

    dataRootItemId? : number;

    highlightedLayoutElementId? : number;

    onHighlightLayoutElement? : ( id? : number ) => void;

    selectedLayoutElementId? : number;

    /**
     * Callback if a layout element is selected.
     *
     * @param id
     */
    onSelectLayoutElement? : ( id? : number ) => void;

    expandedKeys? : TreeExpandedKeysType;

    onDeleteTreeNode? : ( nodeItemId : number ) => void;
};
export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

export type AllProps = Required<DefaultProps> & Props;

export type ReferenceType = {
    addTreeNode : ( parentNodeKey : string, type : 'TreeNode' | 'DynamicNode', dataObjectId? : number ) => Item | undefined
    dataStructure? : DataStructure;
    clearRootItemData : () => void;
    onDragStart : ( nodeKey : string, event : React.DragEvent<HTMLElement> ) => void;
    onReload : () => void;
};

export type ElusiveData = PanelElusiveDataState;


const setParam = origSetParam as <K extends keyof AllProps>( panelKey : string, paramKey : K, paramValue : AllProps[K] ) => SetParamAction;
const setParams = origSetParams as ( panelKey : string, panelProps : Partial<AllProps> ) => SetParamsAction;


const TreeDesigner = React.forwardRef<ReferenceType, ComponentProps>( ( props : ComponentProps, forwardedRef ) => {
    const dispatch : AppDispatch                    = useDispatch(),
          {
              additionalElusiveDataContexts = defaultProps.additionalElusiveDataContexts,
              dataContext,
              dataRootItemId,
              expandedKeys,
              highlightedLayoutElementId,
              mode                          = defaultProps.mode,
              onDeleteTreeNode,
              onHighlightLayoutElement,
              onSelectLayoutElement,
              parentUid,
              readOnly                      = defaultProps.readOnly,
              rootItemId,
              scrollLeft                    = defaultProps.scrollLeft,
              scrollTop                     = defaultProps.scrollTop,
              selectedLayoutElementId,
              uid
          }                                         = props,
          dataDataContext                           = `${uid}_data`,
          treeUid                                   = `${uid}_tree`,
          user                                      = useSelector(
              ( state : StoreState ) => state.session?.user ? state.session.user : undefined
          ),
          messageCatalogTimestamp                   = useSelector(
              ( state : StoreState ) => state.messagecatalog?.timestamp ? state.messagecatalog.timestamp : 0
          ),
          metaModelTimestamp                        = useSelector(
              ( state : StoreState ) => state.metamodel?.timestamp ? state.metamodel.timestamp : 0
          ),
          subPanelProps                             = useSelector(
              ( state : StoreState ) => getSubPanelProps( props.uid, state )?.subPanelProps
          ),
          allElusiveData                            = useSelector(
              ( state : StoreState ) => state.elusiveData
          ),
          treeRef                                   = useRef<Tree>( null ),
          contextMenu                               = useRef<ContextMenu | null>( null ),
          nodeButtons                               = useRef<NodeButtons>( {} ),
          nodeButtonPanels                          = useRef<NodeButtonPanels>( {} ),
          ignoreSelectionChange                     = useRef<boolean>( false ),
          justCleanedRootItemData                   = useRef<boolean>( false ),
          dragInfo                                  = useRef<TreeDragInfo | null>( null ),
          onBackgroundClickRef                      = useRef<MouseEventHandler | null>( null ),
          onMouseMoveRef                            = useRef<MouseEventHandler | null>( null ),
          onMouseUpRef                              = useRef<MouseEventHandler | null>( null ),
          scrollIntervalId                          = useRef<NodeJS.Timeout | undefined>(),
          lastDisplayTreeDataUpdate                 = useRef<number>( 0 ),
          pendingDisplayTreeDataRAF                 = useRef<number>( 0 ),
          currentScrollTop                          = useRef<number>( scrollTop ),
          dragGhostElementRef                       = useRef<HTMLDivElement | null>( null ),
          [ contextMenuNode, setContextMenuNode ]   = useState<string | undefined>( undefined ),
          [ contextMenuModel, setContextMenuModel ] = useState<MenuItem[]>( [] ),
          [ dropZonesRect, setDropZonesRect ]       = useState<DropZonesRect | null>( null ),
          [ displayTreeData, setDisplayTreeData ]   = useState<PrimeTreeNode[] | null>( null ),
          [ dragGhostData, setDragGhostData ]       = useState<DragGhostData | null>( null ),
          language                                  = useMemo(
              () => {
                  return user?.language ?? '';
              },
              [
                  user
              ]
          ),
          globalDataVersions                        = useMemo(
              () => {
                  return JSON.stringify( allElusiveData?.[ dataContext ]?.data ?? {} );
              },
              [
                  allElusiveData?.[ dataContext ]?.data
              ]
          ),
          dynamicNodeOrderPropertySet               = useMemo(
              () => metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'DynamicNode', 'Order' )!,
              [
                  metaModelTimestamp
              ]
          ),
          treeNodeOrderPropertySet                  = useMemo(
              () => metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'TreeNode', 'Order' )!,
              [
                  metaModelTimestamp
              ]
          ),
          activeStatusId                            = useMemo(
              () => metaModel[ 'CFG' ].getStatus( 'active' )!.id,
              [
                  metaModelTimestamp
              ]
          ),
          treeViewItem                              = useMemo(
              () => {
                  return global.data[ dataContext ]?.items[ rootItemId ];
              },
              [
                  dataContext,
                  rootItemId,
                  globalDataVersions
              ]
          ),
          treeView                                  = useMemo(
              () => {
                  let retVal : TreeView | undefined = undefined;

                  if ( treeViewItem ) {
                      retVal = dataExtraction.extractTreeViewData( treeViewItem, dataContext, true );
                  }

                  return retVal;
              },
              [
                  dataContext,
                  globalDataVersions,
                  treeViewItem
              ]
          ),
          treeNodeMap                               = useMemo(
              () => {
                  const retVal : TreeNodeMap = new Map(),
                        addRecursive         = ( nodes : ( DynamicNode | TreeNode )[] ) => {
                            for ( const node of nodes ) {
                                if ( !retVal.has( node.uniqueId ) ) {
                                    retVal.set( node.uniqueId, node );
                                }

                                if ( node.nodes ) {
                                    addRecursive( node.nodes );
                                }
                            }
                        };

                  if ( treeView?.nodes ) {
                      addRecursive( treeView.nodes );
                  }

                  return retVal;
              },
              [
                  treeView?.nodes
              ]
          ),
          dataObjects                               = useMemo(
              () => {
                  let retVal : DataObjectProperties[] = [];

                  if ( treeView?.dataStructure ) {
                      retVal = extractDataObjectsFromDataStructure( treeView.dataStructure );
                  }

                  return retVal;
              },
              [
                  treeView?.dataStructure
              ]
          ),
          dataSchema                                = useMemo(
              () => {
                  return ( treeView?.dataStructure?.item.objectSchema?.toUpperCase() ?? 'DATA' ) as Schema;
              },
              [
                  treeView?.dataStructure
              ]
          ),
          previewId                                 = useMemo(
              () => {
                  return `preview-${dataRootItemId}`;
              },
              [
                  dataRootItemId
              ]
          ),
          previewUid                                = useMemo(
              () => {
                  return `${uid}_${previewId}`;
              },
              [
                  previewId,
                  uid
              ]
          ),
          orderNodes                                = useCallback(
              ( node1 : TreeNode | DynamicNode, node2 : TreeNode | DynamicNode ) => {
                  if ( ( node1.data.order ?? 0 ) > ( node2.data.order ?? 0 ) ) {
                      return 1;
                  }
                  else if ( ( node2.data.order ?? 0 ) > ( node1.data.order ?? 0 ) ) {
                      return -1;
                  }
                  else {
                      return 0;
                  }
              },
              []
          ),
          addTreeNode                               = useCallback(
              ( parentNodeKey : string, type : 'TreeNode' | 'DynamicNode', dataObjectId? : number ) : Item | undefined => {
                  const parentTreeNode = treeNodeMap.get( parentNodeKey ),
                        parentItem     = parentTreeNode ? global.data[ dataContext ]?.items[ parentTreeNode.data.id ] : undefined;

                  let retVal : Item | undefined = undefined;

                  if ( parentTreeNode && parentItem ) {
                      let order = 10;

                      if ( parentTreeNode.nodes ) {
                          parentTreeNode.nodes.sort( orderNodes );

                          for ( const node of parentTreeNode.nodes ?? [] ) {
                              if ( node.data.order !== order ) {
                                  const propertyTypeIdString = String( node.type === 'TreeNode'
                                      ? treeNodeOrderPropertySet.propertyTypeId
                                      : dynamicNodeOrderPropertySet.propertyTypeId );

                                  dispatch( updateItem(
                                      dataContext,
                                      node.data.id,
                                      {
                                          properties: {
                                              [ propertyTypeIdString ]: {
                                                  value: order
                                              }
                                          }
                                      },
                                      additionalElusiveDataContexts
                                  ) );
                              }

                              order += 10;
                          }
                      }

                      // add node item
                      retVal = dispatch( addItem(
                          dataContext,
                          {
                              itemTypeId: metaModel[ 'CFG' ].getItemType( type )!.id,
                              key       : `${treeViewItem.key}:${type}:${uuidV4().substring( 0, 8 )}`,
                              name      : type,
                              statusId  : activeStatusId,
                              properties: [
                                  {
                                      propertySetId: type === 'TreeNode' ? treeNodeOrderPropertySet.id : dynamicNodeOrderPropertySet.id,
                                      statusId     : activeStatusId,
                                      value        : order
                                  }
                              ]
                          },
                          'CFG',
                          additionalElusiveDataContexts
                      ) );

                      // add reference to parent
                      dispatch( addReference(
                          dataContext,
                          {
                              srcItemId     : retVal.id,
                              destItemId    : parentItem.id,
                              referenceSetId: metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType(
                                  type,
                                  parentItem.itemTypeId,
                                  parentItem.itemType.name === 'TreeView'
                                      ? 'belongsTo'
                                      : 'isChildOf',
                                  'src'
                              )![ 0 ].id,
                              statusId      : activeStatusId
                          },
                          'CFG',
                          additionalElusiveDataContexts
                      ) );

                      // add reference to DataObject
                      if ( type === 'DynamicNode' && dataObjectId ) {
                          dispatch( addReference(
                              dataContext,
                              {
                                  srcItemId     : dataObjectId,
                                  destItemId    : retVal.id,
                                  referenceSetId: metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType(
                                      'DataObject',
                                      'DynamicNode',
                                      'providesDataFor',
                                      'src'
                                  )![ 0 ].id,
                                  statusId      : activeStatusId
                              },
                              'CFG',
                              additionalElusiveDataContexts
                          ) );
                      }

                      dispatch( setParam(
                          uid,
                          'expandedKeys',
                          {
                              ...expandedKeys,
                              [ parentNodeKey ]: true
                          }
                      ) );
                  }

                  return retVal;
              },
              [
                  activeStatusId,
                  additionalElusiveDataContexts,
                  dataContext,
                  dynamicNodeOrderPropertySet,
                  expandedKeys,
                  treeNodeMap,
                  treeNodeOrderPropertySet,
                  treeViewItem,
                  uid
              ]
          ),
          deleteOrUndeleteTreeNode                  = useCallback(
              ( nodeKey : string ) => {
                  const treeNode = treeNodeMap.get( nodeKey );

                  if ( !treeNode ) {
                      return;
                  }

                  const nodeItem          = global.data[ dataContext ]?.items[ treeNode.data.id ],
                        nodeItemDeleted   = nodeItem.isDeleted(),
                        referenceToParent = global.data[ dataContext ]?.references?.[ nodeItem.references.find(
                            ( referenceId ) => {
                                const reference = global.data[ dataContext ]?.references?.[ referenceId ];

                                let retVal : boolean = false;

                                if (
                                    reference
                                    && reference.srcItemId === nodeItem.id
                                    && (
                                        reference.referenceSet.referenceType.name === 'isChildOf'
                                        || (
                                            reference.referenceSet.referenceType.name === 'belongsTo'
                                            && reference.referenceSet.srcItemType.name === 'TreeView'
                                        )
                                    )
                                ) {
                                    retVal = true;
                                }

                                return retVal;
                            }
                        ) ?? 0 ];

                  const recursiveToggleChildNodes = ( itemToToggleChildrenOf : Item, isUndelete : boolean ) => {
                      for ( const referenceId of itemToToggleChildrenOf.references ) {
                          const reference = global.data[ dataContext ]?.references?.[ referenceId ];

                          if (
                              reference
                              && reference.destItemId === itemToToggleChildrenOf.id
                              && reference.referenceSet.referenceType.name === 'isChildOf'
                          ) {
                              const srcItem = global.data[ dataContext ].items[ reference.srcItemId ];

                              if ( isUndelete ) {
                                  dispatch( updateReference(
                                      dataContext,
                                      reference.id,
                                      { statusId: reference.attributesModified.get( 'statusId' ) as number },
                                      additionalElusiveDataContexts
                                  ) );

                                  dispatch( updateItem(
                                      dataContext,
                                      srcItem.id,
                                      { statusId: srcItem.attributesModified.get( 'statusId' ) as number },
                                      additionalElusiveDataContexts
                                  ) );
                              }
                              else {
                                  dispatch( deleteReference(
                                      dataContext,
                                      reference.id,
                                      true,
                                      additionalElusiveDataContexts,
                                      false
                                  ) );

                                  dispatch( deleteItem(
                                      dataContext,
                                      srcItem.id,
                                      true,
                                      additionalElusiveDataContexts,
                                      false,
                                      false
                                  ) );
                              }

                              recursiveToggleChildNodes( srcItem, isUndelete );
                          }
                      }
                  };

                  // undelete
                  if ( nodeItemDeleted ) {
                      if ( referenceToParent ) {
                          dispatch( updateReference(
                              dataContext,
                              referenceToParent.id,
                              { statusId: referenceToParent.attributesModified.get( 'statusId' ) as number },
                              additionalElusiveDataContexts
                          ) );
                      }

                      dispatch( updateItem(
                          dataContext,
                          nodeItem.id,
                          { statusId: nodeItem.attributesModified.get( 'statusId' ) as number },
                          additionalElusiveDataContexts
                      ) );

                      recursiveToggleChildNodes( nodeItem, true );
                  }

                  // delete
                  else {
                      if ( referenceToParent ) {
                          dispatch( deleteReference(
                              dataContext,
                              referenceToParent.id,
                              true,
                              additionalElusiveDataContexts,
                              false
                          ) );
                      }

                      dispatch( deleteItem(
                          dataContext,
                          nodeItem.id,
                          true,
                          additionalElusiveDataContexts,
                          false,
                          false
                      ) );

                      recursiveToggleChildNodes( nodeItem, false );

                      // deselect if selected
                      if ( selectedLayoutElementId === nodeItem.id ) {
                          onSelectLayoutElement?.();
                      }

                      onDeleteTreeNode?.( nodeItem.id );

                      onHighlightLayoutElement?.();
                  }
              },
              [
                  additionalElusiveDataContexts,
                  dataContext,
                  onHighlightLayoutElement,
                  onSelectLayoutElement,
                  selectedLayoutElementId,
                  treeNodeMap
              ]
          ),
          expandOrCollapseBranch                    = useCallback(
              ( event : MenuItemCommandEvent ) => {
                  const expand   = event.item.data.expand,
                        treeNode = treeNodeMap.get( event.item.data.nodeKey );

                  if ( uid && treeNode ) {
                      const newExpandedKeys = { ...expandedKeys },
                            expandNode      = ( node : ( TreeNode | DynamicNode ) ) => {
                                if ( node.nodes?.length ) {
                                    if ( expand ) {
                                        newExpandedKeys[ node.uniqueId ] = true;
                                    }
                                    else {
                                        delete newExpandedKeys[ node.uniqueId ];
                                    }

                                    for ( const child of node.nodes ) {
                                        expandNode( child );
                                    }
                                }
                            };

                      expandNode( treeNode );

                      dispatch( setParam( uid, 'expandedKeys', Object.keys( newExpandedKeys ).length ? newExpandedKeys : undefined ) );
                  }
              },
              [
                  expandedKeys,
                  treeNodeMap,
                  uid
              ]
          ),
          buildContextMenu                          = useCallback(
              ( nodeKey : string, showOnly : false | 'add' | 'delete' = false ) : MenuItem[] => {
                  const retVal : MenuItem[] = [],
                        treeNode            = treeNodeMap.get( nodeKey ),
                        treeNodeItem        = treeNode
                            ? global.data[ dataContext ]?.items[ treeNode.data.id ]
                            : undefined,
                        treeNodeReference   = treeNode
                            ? global.data[ dataContext ]?.references[ treeNode.referenceData.id ]
                            : undefined,
                        treeNodeParentItem  = treeNodeReference
                            ? global.data[ dataContext ]?.items[ treeNodeReference.destItemId ]
                            : undefined;

                  let buttonId : string,
                      menuItemId : string;

                  if ( !readOnly && treeNode && treeNodeItem ) {
                      if ( !showOnly && treeNode.nodes?.length ) {
                          retVal.push( {
                              id     : `${nodeKey}_expandAll`,
                              label  : __( 'YN.base.frontend.treeDesigner.node.expandAll.label' ),
                              icon   : 'far fa-plus-square yn-info',
                              data   : {
                                  expand: true,
                                  nodeKey
                              },
                              command: expandOrCollapseBranch
                          } );

                          retVal.push( {
                              id     : `${nodeKey}_collapseAll`,
                              label  : __( 'YN.base.frontend.treeDesigner.node.collapseAll.label' ),
                              icon   : 'far fa-minus-square yn-info',
                              data   : {
                                  expand: false,
                                  nodeKey
                              },
                              command: expandOrCollapseBranch
                          } );

                      }

                      if ( showOnly !== 'delete' && !treeNodeItem.isDeleted() ) {
                          buttonId = `${uid}_${nodeKey}_addButton`;

                          if ( retVal.length ) {
                              retVal.push( { separator: true } );
                          }

                          menuItemId = `${nodeKey}_addTreeNode`;
                          retVal.push( {
                              id     : menuItemId,
                              label  : __( 'YN.base.frontend.treeDesigner.node.addTreeNode.label' ),
                              icon   : 'pi pi-plus-circle yn-add',
                              data   : {
                                  buttonId,
                                  menuItemId
                              },
                              command: () => {
                                  addTreeNode( nodeKey, 'TreeNode' );
                              }
                          } );

                          menuItemId = `${nodeKey}_addDynamicNode`;
                          retVal.push( {
                              id     : menuItemId,
                              label  : __( 'YN.base.frontend.treeDesigner.node.addDynamicNode.label' ),
                              icon   : 'pi pi-plus-circle yn-add',
                              data   : {
                                  buttonId,
                                  menuItemId
                              },
                              command: () => {
                                  addTreeNode( nodeKey, 'DynamicNode' );
                              }
                          } );
                      }

                      if ( showOnly !== 'add' && !treeNodeParentItem?.isDeleted() ) {
                          buttonId = `${uid}_${nodeKey}_deleteButton`;

                          if ( retVal.length ) {
                              retVal.push( { separator: true } );
                          }

                          const isDeleted    = treeNodeItem.isDeleted(),
                                confirmLabel = isDeleted
                                    ? __( `YN.base.designer.tree.item.undelete.text` )
                                    : __( 'YN.base.frontend.treeDesigner.node.delete.confirmQuestion', { itemKey: treeNodeItem.key, itemName: treeNodeItem.name, itemDisplayAttributeValue: treeNodeItem.displayAttributeValue } );

                          menuItemId = isDeleted
                              ? `${nodeKey}_undeleteItem`
                              : `${nodeKey}_deleteItem`;

                          retVal.push( {
                              id     : menuItemId,
                              label  : isDeleted
                                  ? __( 'YN.base.frontend.treeDesigner.node.undelete.label' )
                                  : __( 'YN.base.frontend.treeDesigner.node.delete.label' ),
                              icon   : isDeleted
                                  ? 'pi pi-plus-circle yn-dirty'
                                  : 'pi pi-minus-circle yn-delete',
                              data   : {
                                  buttonId,
                                  menuItemId,
                                  content: <Panel
                                      key={menuItemId}
                                      id={`${menuItemId}_panel`}

                                      style={{ width: '25rem' }}
                                      className="yn-confirm-option-panel"
                                  >
                                      <label>
                                          <span className="pi pi-question-circle yn-blue"></span>
                                          {confirmLabel}
                                      </label>
                                      <Button
                                          label={__( 'YN.base.common.no' )}
                                          className="p-button-sm p-button-secondary mr-3"
                                          rounded={false}
                                          text={false}
                                          outlined={true}
                                          onClick={() => {
                                              nodeButtons.current[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                                          }}
                                      />
                                      <Button
                                          label={__( 'YN.base.common.yes' )}
                                          className="p-button-sm p-confirm-popup-accept"
                                          rounded={false}
                                          text={false}
                                          outlined={true}
                                          onClick={() => {
                                              deleteOrUndeleteTreeNode( nodeKey );

                                              nodeButtons.current[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                                          }}
                                      />
                                  </Panel>
                              },
                              command: ( e : MenuItemCommandEvent ) => {
                                  const event = e.originalEvent as React.MouseEvent;

                                  if ( !event.altKey ) {
                                      nodeButtons.current?.[ buttonId! ].content?.expand( event );
                                  }
                                  else {
                                      deleteOrUndeleteTreeNode( nodeKey );
                                  }
                              }
                          } );
                      }
                  }

                  return retVal;
              },
              [
                  addTreeNode,
                  dataContext,
                  deleteOrUndeleteTreeNode,
                  expandOrCollapseBranch,
                  readOnly,
                  treeNodeMap,
                  uid
              ]
          ),
          buildTreeNodeButton                       = useCallback(
              ( nodeKey : string, type : 'add' | 'delete' ) => {
                  const contentMenuItems = buildContextMenu( nodeKey, type );

                  let retVal : React.ReactElement,
                      buttonId : string;

                  if ( contentMenuItems.length === 1 ) {
                      const getContent = () => {
                          if ( typeof contentMenuItems[ 0 ].data.content === 'function' ) {
                              return contentMenuItems[ 0 ].data.content();
                          }
                          else {
                              return contentMenuItems[ 0 ].data.content;
                          }
                      };

                      buttonId = contentMenuItems[ 0 ].data.buttonId!;

                      nodeButtons.current[ buttonId ] = {
                          justInteracted: false
                      };

                      retVal = <ContentButton
                          key={buttonId}
                          id={buttonId}

                          ref={el => {
                              if ( el ) {
                                  nodeButtons.current[ buttonId ] = {
                                      ...nodeButtons.current[ buttonId ],
                                      content: el
                                  };
                              }
                          }}

                          icon={contentMenuItems[ 0 ].icon}
                          rounded={true}
                          text={true}
                          horizontalContentAlign="left"
                          verticalContentAlign="bottom"

                          onExpand={( event ) => {
                              const innerRetVal = !!getContent();

                              ignoreSelectionChange.current = innerRetVal;

                              if ( event.altKey || !innerRetVal ) {
                                  contentMenuItems[ 0 ].command?.( {
                                      originalEvent: new MouseEvent( 'click', { altKey: event.altKey } ) as unknown as React.MouseEvent,
                                      item         : contentMenuItems[ 0 ]
                                  } );
                              }

                              return innerRetVal;
                          }}

                          onCollapse={( _event, isOutsideClicked ) => {
                              const innerRetVal = !nodeButtons.current[ buttonId ].justInteracted && isOutsideClicked;

                              nodeButtons.current[ buttonId ].justInteracted = false;

                              ignoreSelectionChange.current = true;

                              return innerRetVal;
                          }}

                          content={getContent}
                      />;
                  }
                  else if ( contentMenuItems.length ) {
                      buttonId = contentMenuItems[ 0 ].data.buttonId!;

                      nodeButtons.current[ buttonId ] = {
                          justInteracted: false
                      };

                      const buttonContent : React.ReactNode[] = [];

                      for ( let i = 0; i < contentMenuItems.length; i++ ) {
                          const menuItemId = contentMenuItems[ i ].data.menuItemId!,
                                content    = typeof contentMenuItems[ i ].data.content === 'function'
                                    ? contentMenuItems[ i ].data.content()
                                    : contentMenuItems[ i ].data.content;

                          delete nodeButtonPanels.current[ menuItemId ];

                          buttonContent.push(
                              <Panel
                                  key={menuItemId}
                                  id={`${menuItemId}_panel`}

                                  ref={el => {
                                      if ( el ) {
                                          nodeButtonPanels.current[ menuItemId ] = el;
                                      }
                                  }}

                                  header={
                                      <Button
                                          label={contentMenuItems[ i ].label}
                                          icon={contentMenuItems[ i ].icon}
                                          rounded={false}
                                          text={true}
                                          className="yn-menu-button yn-no-rounded-borders"
                                          onClick={( event ) => {
                                              if ( event.altKey || !contentMenuItems[ i ].data.content ) {
                                                  contentMenuItems[ i ].command?.( {
                                                      originalEvent: new MouseEvent( 'click', { altKey: event.altKey } ) as unknown as React.MouseEvent,
                                                      item         : contentMenuItems[ i ]
                                                  } );

                                                  nodeButtons.current[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                                              }
                                              else {
                                                  for ( const dPanelId of Object.keys( nodeButtonPanels.current ) ) {
                                                      if ( dPanelId !== menuItemId ) {
                                                          nodeButtonPanels.current[ dPanelId ].collapse( event );
                                                      }
                                                      else {
                                                          nodeButtonPanels.current[ dPanelId ].toggle( event );
                                                      }
                                                  }
                                              }
                                          }}
                                      />
                                  }

                                  toggleable={true}
                                  collapsed={true}
                                  className="yn-confirm-option-panel"
                                  pt={{
                                      root: {
                                          style: {
                                              padding: '0'
                                          }
                                      }
                                  }}
                              >
                                  {content}
                              </Panel>
                          );
                      }

                      retVal = <ContentButton
                          key={buttonId}
                          id={buttonId}

                          ref={el => {
                              if ( el ) {
                                  nodeButtons.current[ buttonId ] = {
                                      ...nodeButtons.current[ buttonId ],
                                      content: el
                                  };
                              }
                          }}

                          icon={contentMenuItems[ 0 ].icon}
                          rounded={true}
                          text={true}
                          horizontalContentAlign="left"
                          verticalContentAlign="bottom"

                          content={
                              <Panel
                                  style={{ width: '25rem' }}
                                  pt={{
                                      content: {
                                          style: {
                                              border : 'none',
                                              padding: '.5rem 1rem'
                                          }
                                      }
                                  }}
                              >
                                  {buttonContent}
                              </Panel>
                          }
                      />;
                  }
                  else {
                      return null;
                  }

                  return retVal;
              },
              [
                  buildContextMenu
              ]
          ),
          buildTreeData                             = useCallback(
              ( nodes : ( DynamicNode | TreeNode )[] ) => {
                  const retVal : ExtendTreeNode[] = [];

                  for ( const node of nodes ) {
                      const nodeItem      = global.data[ dataContext ]?.items[ node.data.id ],
                            nodeReference = global.data[ dataContext ]?.references[ node.referenceData.id ];

                      // do not process if only the reference is deleted but not the item (node was moved not deleted)
                      if ( !nodeItem || ( nodeReference.isDeleted() && !nodeItem.isDeleted() ) ) {
                          continue;
                      }

                      const isDynamicNode = nodeItem.itemType.name === 'DynamicNode',
                            classNames    = [ 'yn-designertree-node' ];

                      if ( isDynamicNode ) {
                          if (
                              treeView?.dataStructure?.origId
                              && !( ( node as DynamicNode ).dataObjectIds ?? [] ).includes( treeView.dataStructure.origId )
                          ) {
                              classNames.push( 'yn-treedesigner-node-dynamic' );
                          }
                          else {
                              classNames.push( 'yn-treedesigner-node-dynamic-root' );
                          }
                      }
                      else {
                          classNames.push( 'yn-treedesigner-node-static' );
                      }

                      let label : string                                        = node.messages?.label && typeof node.messages.label === 'string'
                              ? __( node.messages.label )
                              : node.data.name ?? '',
                          icon : string | ( () => React.ReactNode ) | undefined = node.images?.icon ?? node.data.iconClass
                              ? resolveLigatureIcon( `yn-tree-icon ${node.images?.icon ?? ''} ${node.data.iconClass ?? ''}` )
                              : undefined;

                      if ( isDynamicNode ) {
                          const dataObject                = dataObjects.find(
                                    oD => ( ( node as DynamicNode ).dataObjectIds ?? [] ).includes( oD.origId )
                                ),
                                dataObjectItemTypeNameRaw = dataObject?.item.objectType ?? '',
                                excludeDataObjectItemType = dataObjectItemTypeNameRaw.startsWith( '!' ),
                                dataObjectItemTypeName    = excludeDataObjectItemType
                                    ? dataObjectItemTypeNameRaw.substring( 1 )
                                    : dataObjectItemTypeNameRaw,
                                dataObjectItemType        = metaModel[ dataSchema ].getItemType( dataObjectItemTypeName );

                          if ( label === ( node.data.name ?? '' ) && !node.messages?.label && !excludeDataObjectItemType && dataObjectItemType ) {
                              label = `$\{item.${dataObjectItemType.displayAttribute}}`;
                          }

                          if ( dataObjectItemType ) {
                              label = `${excludeDataObjectItemType ? '≠ ' : ''}${dataObjectItemType.label}: ${label}`;
                          }
                          else {
                              label = `${dataObjectItemTypeNameRaw}: ${label}`;
                          }

                          if ( !icon && !excludeDataObjectItemType && dataObjectItemType ) {
                              icon = resolveLigatureIcon( `yn-tree-icon ${dataObjectItemType.icon}` );
                          }
                          else if ( !icon ) {
                              icon = 'yn-icon fa fa-shapes';
                          }
                      }

                      retVal.push( {
                          id        : node.uniqueId,
                          key       : node.uniqueId,
                          label,
                          data      : node,
                          icon,
                          leaf      : !node.nodes?.length,
                          selectable: !nodeItem.isDeleted(),
                          deleted   : nodeItem.isDeleted(),
                          dirty     : nodeItem.isModified(),
                          error     : allElusiveData?.[ `${parentUid}_form-${nodeItem.id}` ]?.errorData,
                          children  : node.nodes?.length
                              ? buildTreeData( node.nodes )
                              : undefined,
                          className : classNames.join( ' ' ),
                          buttons   : [
                              buildTreeNodeButton( node.uniqueId, 'add' ),
                              buildTreeNodeButton( node.uniqueId, 'delete' )
                          ]
                      } );
                  }

                  return retVal;
              },
              [
                  allElusiveData,
                  buildTreeNodeButton,
                  dataContext,
                  dataObjects,
                  dataSchema,
                  metaModelTimestamp,
                  parentUid,
                  treeView
              ]
          ),
          treeData                                  = useMemo(
              () => {
                  let retVal : PrimeTreeNode[] = [];

                  if ( treeView?.nodes?.length ) {
                      retVal = buildTreeData( treeView.nodes );
                  }

                  return retVal;
              },
              [
                  buildTreeData,
                  treeView?.nodes
              ]
          ),
          moveTreeNode                              = useCallback(
              ( dragTreeDataNode : ExtendTreeNode, dragTreeData : PrimeTreeNode[] ) => {
                  const recursiveFindParent   = (
                            treeDataToSearchParentIn : PrimeTreeNode[],
                            treeDataNodeToFindParentFor : PrimeTreeNode,
                            parentTreeDataNode : PrimeTreeNode | 'root' = 'root'
                        ) : PrimeTreeNode | 'root' | undefined => {
                            let retVal : PrimeTreeNode | 'root' | undefined = undefined;

                            for ( const treeDataNode of treeDataToSearchParentIn ) {
                                if ( treeDataNode.key === treeDataNodeToFindParentFor.key ) {
                                    retVal = parentTreeDataNode;
                                }
                                else if ( treeDataNode.children ) {
                                    retVal = recursiveFindParent( treeDataNode.children, treeDataNodeToFindParentFor, treeDataNode );
                                }

                                if ( retVal ) {
                                    break;
                                }
                            }

                            return retVal;
                        },
                        oldParentTreeDataNode = recursiveFindParent( treeData, dragTreeDataNode ),
                        newParentTreeDataNode = recursiveFindParent( dragTreeData, dragTreeDataNode );

                  if ( oldParentTreeDataNode && newParentTreeDataNode ) {
                      const nodeItem          = global.data[ dataContext ].items[ dragTreeDataNode.data.data.id ],
                            oldParentNodeItem = oldParentTreeDataNode === 'root'
                                ? global.data[ dataContext ].items[ rootItemId ]
                                : global.data[ dataContext ].items[ oldParentTreeDataNode.data.data.id ],
                            newParentNodeItem = newParentTreeDataNode === 'root'
                                ? global.data[ dataContext ].items[ rootItemId ]
                                : global.data[ dataContext ].items[ newParentTreeDataNode.data.data.id ];

                      let order = 10;

                      if ( oldParentNodeItem !== newParentNodeItem ) {

                          // remove reference between oldParent and dragged node
                          for ( const referenceId of oldParentNodeItem.references ) {
                              const reference = global.data[ dataContext ].references[ referenceId ];

                              if (
                                  !reference?.isDeleted()
                                  && reference.srcItemId === dragTreeDataNode.data.data.id
                                  && reference.destItemId === oldParentNodeItem.id
                                  && (
                                      ( oldParentNodeItem.itemType.name === 'TreeView' && reference.referenceSet.referenceType.name === 'belongsTo' )
                                      || ( oldParentNodeItem.itemType.name !== 'TreeView' && reference.referenceSet.referenceType.name === 'isChildOf' )
                                  )
                              ) {
                                  dispatch( deleteReference(
                                      dataContext,
                                      referenceId,
                                      true,
                                      additionalElusiveDataContexts
                                  ) );

                                  break;
                              }
                          }

                          // reorder remaining nodes of oldParent
                          if ( oldParentTreeDataNode === 'root' || oldParentTreeDataNode.children ) {
                              const children : PrimeTreeNode[] = oldParentTreeDataNode === 'root'
                                        ? treeData
                                        : oldParentTreeDataNode.children!,
                                    propertyTypeIdString       = String( nodeItem.itemType.name === 'TreeNode'
                                        ? treeNodeOrderPropertySet.propertyTypeId
                                        : dynamicNodeOrderPropertySet.propertyTypeId );

                              order = 10;

                              for ( const child of children ) {
                                  if ( child.data.data.id !== nodeItem.id ) {
                                      dispatch( updateItem(
                                          dataContext,
                                          child.data.data.id,
                                          {
                                              properties: {
                                                  [ propertyTypeIdString ]: {
                                                      value: order
                                                  }
                                              }
                                          },
                                          additionalElusiveDataContexts
                                      ) );

                                      order += 10;
                                  }
                              }
                          }

                          // add reference between newParen and dragged node
                          dispatch( addReference(
                              dataContext,
                              {
                                  srcItemId     : nodeItem.id,
                                  destItemId    : newParentNodeItem.id,
                                  referenceSetId: metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType(
                                      nodeItem.itemTypeId,
                                      newParentNodeItem.itemTypeId,
                                      newParentNodeItem.itemType.name === 'TreeView'
                                          ? 'belongsTo'
                                          : 'isChildOf',
                                      'src'
                                  )![ 0 ].id,
                                  statusId      : activeStatusId
                              },
                              'CFG',
                              additionalElusiveDataContexts
                          ) );
                      }

                      // reorder nodes of newParent
                      if ( newParentTreeDataNode === 'root' || newParentTreeDataNode.children ) {
                          const children : PrimeTreeNode[] | undefined = newParentTreeDataNode === 'root'
                                    ? treeData
                                    : newParentTreeDataNode.children,
                                propertyTypeIdString                   = String( nodeItem.itemType.name === 'TreeNode'
                                    ? treeNodeOrderPropertySet.propertyTypeId
                                    : dynamicNodeOrderPropertySet.propertyTypeId );

                          order = 10;

                          for ( const child of children ?? [] ) {
                              dispatch( updateItem(
                                  dataContext,
                                  child.data.data.id,
                                  {
                                      properties: {
                                          [ propertyTypeIdString ]: {
                                              value: order
                                          }
                                      }
                                  },
                                  additionalElusiveDataContexts
                              ) );

                              order += 10;
                          }
                      }
                  }
              },
              [
                  activeStatusId,
                  additionalElusiveDataContexts,
                  dataContext,
                  dynamicNodeOrderPropertySet,
                  rootItemId,
                  treeData,
                  treeNodeOrderPropertySet
              ]
          ),
          recursiveCloneTreeData                    = useCallback(
              ( treeDataToClone : PrimeTreeNode[] ) : PrimeTreeNode[] => {
                  const retVal : PrimeTreeNode[] = [];

                  for ( const treeDataNodeToClone of treeDataToClone ) {
                      retVal.push( {
                          ...treeDataNodeToClone,
                          children: treeDataNodeToClone.children ? recursiveCloneTreeData( treeDataNodeToClone.children ) : undefined
                      } );
                  }

                  return retVal;
              },
              []
          ),
          isDescendant                              = useCallback(
              ( treeDataToUse : PrimeTreeNode, nodeKey : string ) : boolean => {
                  for ( const child of treeDataToUse.children ?? [] ) {
                      if ( child.key === nodeKey || isDescendant( child, nodeKey ) ) {
                          return true;
                      }
                  }
                  return false;
              },
              []
          ),
          onReload                                  = () => {
              dispatch( setParam( uid, 'expandedKeys', undefined ) );
          },
          clearRootItemData                         = () => {
              delete global.data[ dataDataContext ];

              dispatch( clearElusiveData( dataDataContext ) );
              dispatch( clearElusiveDataForSubpanels( uid ) );
              dispatch( clearPanelProps( previewUid ) );

              justCleanedRootItemData.current = true;
          },
          getScrollPosition                         = useMemo(
              () => {
                  return _.debounce(
                      () => {
                          const scrollElement = treeRef.current?.getElement()?.querySelector( '.p-tree-container' ) as HTMLElement;

                          if ( scrollElement ) {
                              dispatch( setParams( uid, {
                                  scrollTop : scrollElement.scrollTop ?? 0,
                                  scrollLeft: scrollElement.scrollLeft ?? 0
                              } ) );
                          }
                      },
                      250
                  );
              },
              [
                  uid
              ]
          ),
          onBackgroundClickStable                   = useCallback(
              ( event : MouseEvent ) => {
                  onBackgroundClickRef.current?.( event );
              },
              []
          ),
          onBackgroundClick                         = useCallback(
              ( event : MouseEvent ) => {
                  if (
                      mode === 'design'
                      && !( event.target as HTMLElement ).classList.contains( 'p-button' )
                      && selectedLayoutElementId !== rootItemId
                  ) {
                      onSelectLayoutElement?.( rootItemId );
                  }
              },
              [
                  mode,
                  onSelectLayoutElement,
                  rootItemId,
                  selectedLayoutElementId
              ]
          ),
          onToggleTreeNode                          = useCallback(
              ( event : TreeExpandedEvent ) => {
                  dispatch( setParam( uid, 'expandedKeys', Object.keys( event.value ).length ? event.value : undefined ) );
              },
              [
                  uid
              ]
          ),
          onContextMenuSelectionChange              = useCallback(
              ( event : TreeSelectionEvent ) => {
                  const treeNode = treeNodeMap.get( event.value as string );

                  if ( treeNode ) {
                      setContextMenuNode( treeNode.uniqueId );
                      setContextMenuModel( buildContextMenu( treeNode.uniqueId ) );
                  }
                  else {
                      setContextMenuNode( undefined );
                      setContextMenuModel( [] );
                  }
              },
              [
                  buildContextMenu,
                  treeNodeMap
              ]
          ),
          onContextMenu                             = useCallback(
              ( event : TreeEventNodeEvent ) => {
                  contextMenu.current?.show( event.originalEvent );
              },
              []
          ),
          showDropZones                             = () => {
              const wrapperElement    = document.getElementById( `${treeUid}_wrapper` )?.parentElement?.parentElement,
                    wrapperElementBox = wrapperElement?.getBoundingClientRect();

              if ( wrapperElement && wrapperElementBox ) {
                  setDropZonesRect( {
                      top        : wrapperElementBox.y,
                      height     : wrapperElementBox.height,
                      deleteWidth: wrapperElementBox.x,
                      cancelWidth: document.body.clientWidth - wrapperElementBox.x - wrapperElementBox.width
                  } );
              }
          },
          hideDropZones                             = () => {
              setDropZonesRect( null );
          },
          onMouseMoveStable                         = useCallback(
              ( event : MouseEvent ) => {
                  onMouseMoveRef.current?.( event );
              },
              []
          ),
          onMouseMove                               = useCallback(
              ( event : MouseEvent ) : void => {
                  let endUp : boolean = false;

                  if ( dragInfo.current ) {
                      // set position of drag ghost
                      if ( dragGhostElementRef.current ) {
                          dragGhostElementRef.current.style.left = ( event.pageX - 50 ) + 'px';
                          dragGhostElementRef.current.style.top = ( event.pageY - 50 ) + 'px';
                      }

                      // scroll tree - end up on scrolling
                      const scrollElement    = treeRef.current?.getElement()?.querySelector( '.p-tree-container' ),
                            scrollElementBox = scrollElement?.getBoundingClientRect();

                      if ( scrollIntervalId.current ) {
                          clearInterval( scrollIntervalId.current );
                      }

                      if ( scrollElement && scrollElementBox ) {
                          if ( scrollElement.scrollHeight > scrollElement.clientHeight ) {
                              if (
                                  event.pageX > scrollElementBox.x
                                  && event.pageX < ( scrollElementBox.x + scrollElementBox.width )
                                  && event.pageY < ( scrollElementBox.y + .1 * scrollElement.clientHeight )
                                  && scrollTop > 1
                              ) {
                                  dispatch( setParam( uid, 'scrollTop', Math.max( 0, scrollTop - 25 ) ) );

                                  scrollIntervalId.current = setInterval(
                                      () => {
                                          dispatch( setParam( uid, 'scrollTop', Math.max( 0, currentScrollTop.current - 25 ) ) );
                                      },
                                      25
                                  );

                                  endUp = true;
                              }
                              else if (
                                  event.pageX > scrollElementBox.x
                                  && event.pageX < ( scrollElementBox.x + scrollElementBox.width )
                                  && event.pageY > ( scrollElementBox.y + .9 * scrollElement.clientHeight )
                                  && scrollTop < ( scrollElement.scrollHeight - scrollElement.clientHeight ) - 1
                              ) {
                                  dispatch( setParam( uid, 'scrollTop', Math.min( scrollElement.scrollHeight - scrollElement.clientHeight, scrollTop + 25 ) ) );

                                  scrollIntervalId.current = setInterval(
                                      () => {
                                          dispatch( setParam( uid, 'scrollTop', Math.min( scrollElement.scrollHeight - scrollElement.clientHeight, currentScrollTop.current + 25 ) ) );
                                      },
                                      25
                                  );

                                  endUp = true;
                              }
                          }
                      }

                      // get drag over treeDataNode
                      if ( !endUp ) {
                          const throttledSetDisplayTreeData = ( value : PrimeTreeNode[] | null ) => {
                                    const now = performance.now();

                                    if ( pendingDisplayTreeDataRAF.current ) {
                                        cancelAnimationFrame( pendingDisplayTreeDataRAF.current );
                                        pendingDisplayTreeDataRAF.current = 0;
                                    }

                                    if ( now - lastDisplayTreeDataUpdate.current >= 50 ) {
                                        lastDisplayTreeDataUpdate.current = now;
                                        setDisplayTreeData( value );
                                    }
                                    else {
                                        pendingDisplayTreeDataRAF.current = requestAnimationFrame( () => {
                                            lastDisplayTreeDataUpdate.current = performance.now();
                                            pendingDisplayTreeDataRAF.current = 0;
                                            setDisplayTreeData( value );
                                        } );
                                    }
                                },
                                recursiveFindAndRemove      = ( treeDataToUse : PrimeTreeNode[] ) : boolean => {
                                    const index = treeDataToUse.findIndex( n => n.key === dragInfo.current!.nodeKey );

                                    let retVal : boolean = false;

                                    if ( index !== -1 ) {
                                        retVal = true;
                                        treeDataToUse.splice( index, 1 );
                                    }
                                    else {
                                        for ( const childNode of treeDataToUse ) {
                                            if ( childNode.children ) {
                                                retVal = recursiveFindAndRemove( childNode.children );

                                                if ( retVal ) {
                                                    break;
                                                }
                                            }
                                        }
                                    }

                                    return retVal;
                                },
                                cleanMarker                 = () => {
                                    hideDropZones();

                                    // unmark drag element
                                    dragGhostElementRef.current?.classList.remove(
                                        'yn-treedesigner-drag-ok',
                                        'yn-treedesigner-drag-nothing',
                                        'yn-treedesigner-drag-delete'
                                    );

                                    // remove drag target marker
                                    for ( const nodeKey of treeNodeMap.keys() ) {
                                        const nodeElement = document.getElementById( `${nodeKey}_header` )?.closest( '.yn-designertree-node' );

                                        nodeElement?.classList.remove( 'yn-treedesigner-drag-target' );
                                    }
                                },
                                wrapperElement              = document.getElementById( `${treeUid}_wrapper` )?.parentElement,
                                wrapperElementBox           = wrapperElement?.getBoundingClientRect();

                          cleanMarker();

                          // dragged to delete zone
                          if (
                              wrapperElementBox
                              && event.pageX < wrapperElementBox.x
                              && event.pageY > wrapperElementBox.y
                          ) {
                              const cloned = recursiveCloneTreeData( treeData );

                              showDropZones();

                              recursiveFindAndRemove( cloned );
                              throttledSetDisplayTreeData( cloned );

                              dragInfo.current.dragOverZone = 'delete';
                              delete dragInfo.current.dragOverNodeKey;

                              dragGhostElementRef.current?.classList.add( 'yn-treedesigner-drag-delete' );
                          }
                          // dragged outside designer
                          else if (
                              wrapperElementBox
                              && (
                                  event.pageX > wrapperElementBox.x + wrapperElementBox.width
                                  || event.pageY < wrapperElementBox.y
                              )
                          ) {
                              showDropZones();

                              throttledSetDisplayTreeData( null );

                              dragInfo.current.dragOverZone = 'nothing';
                              delete dragInfo.current.dragOverNodeKey;

                              dragGhostElementRef.current?.classList.add( 'yn-treedesigner-drag-nothing' );
                          }
                          // get TreeNode or DynamicNode under the cursor
                          else {
                              const recursiveFindAndInsert = ( treeDataToUse : PrimeTreeNode[], treeDataNodeKeyDraggedOver : string, position : 'before' | 'after' | 'child' ) : boolean => {
                                        const index = treeDataToUse.findIndex( n => n.key === treeDataNodeKeyDraggedOver );

                                        let retVal : boolean = false;

                                        if ( index !== -1 && dragInfo.current ) {
                                            const insertNodes = [ dragInfo.current.treeDataNode ];

                                            retVal = true;

                                            if ( position === 'before' ) {
                                                treeDataToUse.splice( index, 0, ...insertNodes );
                                            }
                                            else if ( position === 'after' ) {
                                                treeDataToUse.splice( index + 1, 0, ...insertNodes );
                                            }
                                            else if ( position === 'child' ) {
                                                if ( !treeDataToUse[ index ].children ) {
                                                    treeDataToUse[ index ].children = insertNodes;
                                                }
                                                else {
                                                    treeDataToUse[ index ].children!.splice( 0, 0, ...insertNodes );
                                                }
                                            }
                                        }
                                        else {
                                            for ( const childNode of treeDataToUse ) {
                                                if ( childNode.children ) {
                                                    retVal = recursiveFindAndInsert( childNode.children, treeDataNodeKeyDraggedOver, position );

                                                    if ( retVal ) {
                                                        break;
                                                    }
                                                }
                                            }
                                        }

                                        return retVal;
                                    },
                                    nodeElement            = document.elementFromPoint( event.pageX, event.pageY )?.closest( '.yn-designertree-node' ),
                                    nodeKey                = nodeElement?.querySelector( '.yn-designertree-header .yn-treenode-header' )?.id,
                                    nodeElementBox         = nodeElement?.getBoundingClientRect();

                              if (
                                  nodeElement
                                  && nodeKey
                                  && nodeElementBox
                                  && event.pageX > nodeElementBox.x && event.pageX < ( nodeElementBox.x + nodeElementBox.width )
                                  && event.pageY > nodeElementBox.y - 5 && event.pageY < ( nodeElementBox.y + nodeElementBox.height + 5 )
                              ) {
                                  const cloned = recursiveCloneTreeData( treeData ),
                                        y      = event.pageY - nodeElementBox.y;

                                  let position : 'before' | 'after' | 'child';

                                  nodeElement.classList.add( 'yn-treedesigner-drag-target' );

                                  if ( y <= .25 * nodeElementBox.height ) {
                                      position = 'before';
                                  }
                                  else if ( y >= .75 * nodeElementBox.height ) {
                                      position = 'after';
                                  }
                                  else {
                                      position = 'child';

                                      if ( !expandedKeys?.[ nodeKey ] ) {
                                          dispatch( setParam(
                                              uid,
                                              'expandedKeys',
                                              {
                                                  ...expandedKeys,
                                                  [ nodeKey ]: true
                                              }
                                          ) );
                                      }
                                  }

                                  recursiveFindAndRemove( cloned );
                                  recursiveFindAndInsert( cloned, nodeKey, position );
                                  throttledSetDisplayTreeData( cloned );

                                  dragInfo.current.dragOverZone = 'node';
                                  dragInfo.current.dragOverNodeKey = nodeKey;

                                  dragGhostElementRef.current?.classList.add( 'yn-treedesigner-drag-ok' );
                              }
                              else {
                                  dragInfo.current.dragOverZone = 'nothing';
                                  throttledSetDisplayTreeData( null );

                                  dragGhostElementRef.current?.classList.add( 'yn-treedesigner-drag-nothing' );
                              }
                          }
                      }
                  }
              },
              [
                  expandedKeys,
                  scrollTop,
                  treeData,
                  treeNodeMap,
                  uid
              ]
          ),
          onMouseUpStable                           = useCallback(
              ( event : MouseEvent ) => {
                  onMouseUpRef.current?.( event );
              },
              []
          ),
          onMouseUp                                 = useCallback(
              () : void => {
                  // remove listeners for drag control
                  document.querySelector( 'html' )?.removeEventListener( 'mouseup', onMouseUpStable );
                  document.querySelector( 'html' )?.removeEventListener( 'mousemove', onMouseMoveStable );

                  hideDropZones();

                  // cancel any pending throttled display update
                  if ( pendingDisplayTreeDataRAF.current ) {
                      cancelAnimationFrame( pendingDisplayTreeDataRAF.current );
                      pendingDisplayTreeDataRAF.current = 0;
                  }

                  if ( dragInfo.current ) {

                      // delete
                      if ( dragInfo.current.dragOverZone === 'delete' ) {
                          deleteOrUndeleteTreeNode( dragInfo.current.nodeKey );
                      }
                      // move
                      else if ( dragInfo.current.dragOverZone === 'node' && displayTreeData ) {
                          moveTreeNode( dragInfo.current.treeDataNode, displayTreeData );
                      }

                      // clean drag info
                      dragInfo.current = null;

                      // destroy drag ghost (portal will unmount via state)
                      setDragGhostData( null );
                      // reset display tree data
                      setDisplayTreeData( null );
                  }
              },
              [
                  displayTreeData
              ]
          ),
          onDragStart                               = useCallback(
              ( nodeKey : string, event : React.DragEvent<HTMLElement> ) : void => {
                  const recursiveFind = ( treeDataToSearchIn : ExtendTreeNode[] ) : ExtendTreeNode | undefined => {
                            let retVal : ExtendTreeNode | undefined;

                            for ( const treeDataNode of treeDataToSearchIn ) {
                                if ( treeDataNode.key === nodeKey ) {
                                    retVal = treeDataNode;
                                }
                                else if ( treeDataNode.children ) {
                                    retVal = recursiveFind( treeDataNode.children );
                                }

                                if ( retVal ) {
                                    break;
                                }
                            }

                            return retVal;
                        },
                        treeDataNode  = recursiveFind( treeData as ExtendTreeNode[] );

                  if ( treeDataNode?.data ) {
                      // create drag ghost via React portal
                      let icon : React.ReactNode = null;

                      if ( typeof treeDataNode.icon === 'string' ) {
                          icon = getIconSpan( treeDataNode.icon );
                      }
                      else if ( typeof treeDataNode.icon === 'function' ) {
                          icon = ( treeDataNode.icon as ( () => React.ReactElement ) )();
                      }

                      setDragGhostData( {
                          icon,
                          label      : treeDataNode.label,
                          initialLeft: event.pageX - 50,
                          initialTop : event.pageY - 50
                      } );

                      // init drag info
                      dragInfo.current = {
                          nodeKey,
                          treeDataNode,
                          dragOverZone: 'nothing'
                      };

                      // add listeners for drag control
                      document.querySelector( 'html' )?.addEventListener( 'mousemove', onMouseMoveStable );
                      document.querySelector( 'html' )?.addEventListener( 'mouseup', onMouseUpStable );

                      event.preventDefault();
                      event.stopPropagation();
                      event.nativeEvent.preventDefault();
                  }
              },
              [
                  treeData
              ]
          ),
          treeNodeTemplate                          = useCallback(
              ( treeNodeData : PrimeTreeNode ) : React.ReactNode => {
                  const nodeData        = treeNodeData as ExtendTreeNode,
                        classNames      = new Set( ( nodeData.className ?? '' ).split( ' ' ).filter( Boolean ) ),
                        labelClassNames = [ 'yn-label' ];

                  let label : string | React.ReactNode = nodeData.label ?? '';

                  // parse label
                  if ( label && typeof label === 'string' && !isInvalidHTML( label as string ) ) {
                      label = parse( label as string );
                  }

                  if ( nodeData.deleted ) {
                      classNames.add( 'yn-default-cursor' );
                      classNames.add( 'yn-deleted' );
                  }

                  else if ( nodeData.error?.count ) {
                      labelClassNames.push( 'yn-error' );
                  }

                  else if ( nodeData.dirty ) {
                      labelClassNames.push( 'yn-dirty' );
                  }

                  if (
                      !dragGhostData
                      && highlightedLayoutElementId
                      && (
                          nodeData.data?.data.id === highlightedLayoutElementId
                          || ( ( nodeData.data as DynamicNode | undefined )?.dataObjectIds ?? [] ).includes( highlightedLayoutElementId )
                      )
                  ) {
                      classNames.add( 'yn-designertree-node-highlighted' );
                  }
                  else {
                      classNames.delete( 'yn-designertree-node-highlighted' );
                  }

                  if ( nodeData.data?.data.id && nodeData.data.data.id === selectedLayoutElementId ) {
                      classNames.add( 'yn-designertree-node-selected' );
                      classNames.add( 'p-highlight' );
                  }
                  else {
                      classNames.delete( 'yn-designertree-node-selected' );
                      classNames.delete( 'p-highlight' );
                  }

                  if (
                      dragGhostData
                      && dragInfo.current
                      && (
                          nodeData.key === dragInfo.current.nodeKey
                          || isDescendant( dragInfo.current.treeDataNode, nodeData.key )
                      )
                  ) {
                      classNames.add( 'yn-designertree-node-dragged' );
                  }
                  else {
                      classNames.delete( 'yn-designertree-node-dragged' );
                  }

                  nodeData.className = [ ...classNames ].join( ' ' );

                  // build node
                  return <>
                      {( nodeData.tooltip ?? nodeData.error?.tooltip ) &&
                          <Tooltip target={`#${nodeData.key}`} showDelay={500} position="top">
                              {nodeData.tooltip}
                              {nodeData.error?.tooltip &&
                                  <div key="error" className="yn-tooltip-error">
                                      <div className="yn-tooltip-header">
                                          {__( 'YN.base.common.tooltip.error.title' )}
                                      </div>
                                      {nodeData.error.tooltip}
                                  </div>
                              }
                          </Tooltip>
                      }
                      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events */}
                      <div
                          id={`${nodeData.key}_header`}
                          className={`flex flex-column flex-auto yn-designertree-header ${nodeData.icon ? 'yn-designertree-header-with-icon' : ''}`}
                          draggable={!readOnly}
                          onClick={() => {
                              if ( !ignoreSelectionChange.current && nodeData.data?.data.id && !nodeData.deleted ) {
                                  onSelectLayoutElement?.( nodeData.data.data.id );
                              }

                              ignoreSelectionChange.current = false;
                          }}
                          onMouseEnter={() => {
                              if ( nodeData.data?.data.id ) {
                                  onHighlightLayoutElement?.( nodeData.data.data.id );
                              }
                          }}
                          onMouseLeave={() => {
                              onHighlightLayoutElement?.();
                          }}
                          onDragStart={readOnly
                              ? undefined
                              : onDragStart.bind( null, nodeData.key )}
                      >
                          <div id={`${nodeData.key}`} className="yn-treenode-header">
                              <span key="label" className={labelClassNames.join( ' ' )}>{label}</span>
                              {nodeData.buttons}
                          </div>
                      </div>
                  </>;
              },
              [
                  dragGhostData,
                  highlightedLayoutElementId,
                  language,
                  messageCatalogTimestamp,
                  onHighlightLayoutElement,
                  onSelectLayoutElement,
                  readOnly,
                  selectedLayoutElementId
              ]
          ),
          togglerTemplate                           = useCallback(
              ( _node : PrimeTreeNode, options : TreeTogglerTemplateOptions ) => {
                  return <button
                      type="button"
                      onClick={options.onClick}
                      className="p-tree-toggler p-link"
                      tabIndex={-1}
                      aria-label={localeOption( 'aria', '' )?.[ options.expanded ? 'collapseLabel' : 'expandLabel' ]}
                  >
                      <span
                          className={`p-tree-toggler-icon pi pi-fw ${options.expanded ? 'pi-minus-circle' : 'pi-plus-circle'}`}
                          aria-hidden="true"
                      />
                  </button>;
              },
              []
          ),
          tree                                      = useMemo(
              () => {
                  if ( mode === 'design' ) {
                      return <LoadingMaskPanel
                          forComponent={treeUid}
                          className=" yn-view-loadingmask yn-graphview-loadingmask"
                          style={{ width: '100%' }}
                      >
                          <div
                              className="yn-content-menu-container"
                              style={{
                                  width    : '25vw',
                                  maxHeight: '100%',
                                  overflow : 'hidden'
                              }}
                          >
                              <ContextMenu
                                  key={`${treeUid}_contextMenu`}
                                  model={contextMenuModel}
                                  ref={el => contextMenu.current = el}
                                  onHide={() => setContextMenuNode( undefined )}
                                  pt={{
                                      root: {
                                          style: {
                                              minWidth: '17rem'
                                          }
                                      }
                                  }}
                              />
                              <Tree
                                  id={treeUid}

                                  ref={treeRef}

                                  className="yn-menu-tree-lines xyn-node-content-tree yn-designertree yn-designertree-no-frames yn-treedesigner-tree"
                                  contentClassName="yn-tree-container"

                                  value={displayTreeData ?? treeData}

                                  nodeTemplate={treeNodeTemplate}

                                  // the pick is necessary as PrimeReact ignores value of expandedKeys object, and we need collapsed node to false
                                  // as we need to detect what's different from initial loaded state
                                  expandedKeys={expandedKeys}
                                  onToggle={onToggleTreeNode}
                                  togglerTemplate={togglerTemplate}

                                  contextMenuSelectionKey={String( contextMenuNode )}
                                  onContextMenuSelectionChange={onContextMenuSelectionChange}
                                  onContextMenu={onContextMenu}

                                  pt={{
                                      container: {
                                          onScroll: getScrollPosition
                                      }
                                  }}
                              />
                          </div>
                      </LoadingMaskPanel>;
                  }
              },
              [
                  contextMenuModel,
                  contextMenuNode,
                  displayTreeData,
                  expandedKeys,
                  getScrollPosition,
                  mode,
                  onContextMenu,
                  onContextMenuSelectionChange,
                  onToggleTreeNode,
                  togglerTemplate,
                  treeData,
                  treeNodeTemplate,
                  treeUid
              ]
          ),
          preview                                   = useMemo(
              () => {
                  if ( mode !== 'design' && dataRootItemId && !justCleanedRootItemData.current ) {
                      const data = getDataRecursive( dataContext, 'CFG', rootItemId, true );

                      return <div
                          className="yn-content-menu-container"
                          style={{
                              height  : '100%',
                              overflow: 'hidden'
                          }}
                      >
                          <TreeViewPanel
                              {...subPanelProps?.[ previewId ] as TreeViewPanelProps}

                              uid={previewUid}

                              viewKey="designer"

                              schema={dataSchema}
                              dataContext={dataDataContext}
                              rootItemId={dataRootItemId}

                              contentPanelOnly={true}
                              additionalApiParams={{
                                  data: {
                                      dataContext,
                                      items     : data.items.map( i => i.toJSON( 'all' ) ),
                                      references: data.references.map( r => r.toJSON( 'all' ) )
                                  }
                              }}
                          />
                      </div>;
                  }
              },
              [
                  dataContext,
                  dataDataContext,
                  dataRootItemId,
                  dataSchema,
                  mode,
                  previewId,
                  previewUid,
                  rootItemId,
                  subPanelProps
              ]
          ),
          dragGhost                                 = useMemo(
              () => {
                  let retVal : React.ReactPortal | undefined = undefined;

                  if ( dragGhostData ) {
                      retVal = createPortal(
                          <div
                              ref={dragGhostElementRef}
                              id="drag"
                              className="yn-treedesigner-drag"
                              style={{
                                  left: `${dragGhostData.initialLeft}px`,
                                  top : `${dragGhostData.initialTop}px`
                              }}
                          >
                              <div
                                  id="drag_preview"
                                  className="yn-treedesigner-drag-preview p-component yn-fieldlike"
                              >
                                  {dragGhostData.icon}{dragGhostData.label}
                              </div>
                          </div>,
                          document.body
                      );
                  }

                  return retVal;
              },
              [
                  dragGhostData
              ]
          ),
          dropZones                                 = useMemo(
              () => {
                  let retVal : React.ReactPortal | undefined = undefined;

                  if ( dropZonesRect ) {
                      retVal = createPortal(
                          <>
                              <div
                                  id={`${uid}_treedesigner_delete_zone`}
                                  className="yn-treedesigner-delete-zone"
                                  style={{
                                      top   : `${dropZonesRect.top}px`,
                                      width : `${dropZonesRect.deleteWidth}px`,
                                      height: `${dropZonesRect.height}px`
                                  }}
                              />
                              <div
                                  id={`${uid}_treedesigner_cancel_zone`}
                                  className="yn-treedesigner-cancel-zone"
                                  style={{
                                      top   : `${dropZonesRect.top}px`,
                                      width : `${dropZonesRect.cancelWidth}px`,
                                      height: `${dropZonesRect.height}px`
                                  }}
                              />
                          </>,
                          document.body
                      );
                  }

                  return retVal;
              },
              [
                  dropZonesRect,
                  uid
              ]
          ),
          content                                   = useMemo(
              () => {
                  return <div
                      id={`${treeUid}_wrapper`}
                      className="yn-form-card yn-view-card yn-view-card-standalone yn-view-card-standalone-view"
                      style={{ borderColor: 'transparent' }}
                  >
                      <Card
                          style={{ width: mode === 'design' ? '25vw' : '100%' }}
                      >
                          <div
                              className="yn-view-wrapper yn-view-wrapper-graph yn-view-wrapper-card"
                          >
                              <div
                                  className=" yn-view-wrapper-border"
                              >
                                  {tree}
                                  {preview}
                              </div>
                          </div>
                      </Card>
                      {dragGhost}
                      {dropZones}
                  </div>;
              },
              [
                  dragGhost,
                  dropZones,
                  mode,
                  preview,
                  tree,
                  treeUid
              ]
          );

    currentScrollTop.current = scrollTop;
    justCleanedRootItemData.current = false;

    // keep stable references for event listeners used during drag
    onBackgroundClickRef.current = onBackgroundClick;
    onMouseMoveRef.current = onMouseMove;
    onMouseUpRef.current = onMouseUp;

    // expose methods via reference
    useImperativeHandle(
        forwardedRef,
        () => ( {
            addTreeNode,
            dataStructure: treeView?.dataStructure,
            clearRootItemData,
            onDragStart,
            onReload
        } )
    );

    // ( on mount / unmount )
    // add/remove listener for color scheme switch
    // remove the observed element from resizeObserver
    useEffect(
        () => {
            const clickTarget = document.getElementById( `${treeUid}_wrapper` )?.parentElement?.parentElement;

            clickTarget?.addEventListener( 'click', onBackgroundClickStable );

            // unmount
            return () => {
                clickTarget?.removeEventListener( 'click', onBackgroundClickStable );
                clearInterval( scrollIntervalId.current );
            }
        },
        []
    );

    // clear data for rootItemId on switching mode to design
    useEffect(
        () => {
            if ( mode === 'design' ) {
                clearRootItemData();
            }
        },
        [
            mode
        ]
    )

    // auto-expand nodes that have openExpanded set
    useEffect(
        () => {
            if ( treeView?.nodes && !Object.keys( expandedKeys ?? {} ).length ) {
                const newExpandedKeys : TreeExpandedKeysType = {};

                let changed = false;

                const collectAutoExpand = ( nodes : ( DynamicNode | TreeNode )[] ) => {
                    for ( const node of nodes ) {
                        if ( node.data.openExpanded ) {
                            newExpandedKeys[ node.uniqueId ] = true;
                            changed = true;
                        }

                        if ( node.nodes ) {
                            collectAutoExpand( node.nodes );
                        }
                    }
                };

                collectAutoExpand( treeView.nodes );

                if ( changed ) {
                    dispatch( setParam(
                        uid,
                        'expandedKeys',
                        Object.keys( newExpandedKeys ).length ? newExpandedKeys : undefined
                    ) );
                }
            }
        },
        [
            treeView?.nodes,
            expandedKeys,
            uid
        ]
    );

    // set the scroll position (on initial render or change)
    useLayoutEffect(
        () => {
            const scrollElement = treeRef.current?.getElement()?.querySelector( '.p-tree-container' ) as HTMLElement;

            if ( scrollElement ) {
                scrollElement.scrollTop = scrollTop;
                scrollElement.scrollLeft = scrollLeft;
            }
        },
        [
            scrollLeft,
            scrollTop
        ]
    );

    return content;
} );
TreeDesigner.displayName = 'TreeDesigner';

export default TreeDesigner;
